import 'index.scss';
import {render} from 'react-dom';
import {Provider, useDispatch} from 'react-redux';
import isNil from 'lodash/isNil';
import Loader from '@teladoc/fe-ccm/ui/loader/Loader';
import mixpanel from 'mixpanel-browser';
import withResolvers from 'promise.withresolvers';
import {PersistGate} from 'redux-persist/integration/react';
import ErrorBoundary from '@teladoc/pulse/ui/ErrorBoundary';
import PulseApp, {STATUSES} from '@teladoc/pulse/ui/App';
import ApptentiveUtils from '@teladoc/fe-ccm/ui/common/apptentive/apptentive-utils';
import {hasNonEmptyValue} from '@teladoc/fe-ccm/ui/common/utilities/has-non-empty-value';
import UserAPI from '@teladoc/fe-ccm/ui/user/user-api';
import ChatAPI from '@teladoc/fe-ccm/ui/chat/chat-api';
import {config as i18n} from '@teladoc/fe-i18n';
import {useEffect, useState} from 'react';
import {userUpdate} from '@teladoc/fe-ccm/ui/user/user-actions';
import {appUpdate} from '@teladoc/fe-ccm/ui/app/app-actions';
import MixpanelUtils from '@teladoc/fe-ccm/ui/common/utilities/mix-panel';
import {StatsigClient, StatsigProvider} from '@teladoc/statsig';
import EnvironmentUtils from '@livongo/utilities/system/environment';
import StorageUtils from '@livongo/utilities/system/storage';
import Arg from '@livongo/arg';
import APIUtils from '@livongo/utilities/system/api';
import AmplitudeClient from '@livongo/utilities/system/amplitude';
import NewRelicClient from '@livongo/utilities/system/newrelic';
import NewRelicUtils from '@/utils/new-relic-utils';
import {CONDITION_MANAGEMENT_PREFIX, HOME_PATH} from '@/router/bootstrap-paths';
import create from '@/store';
import {getIsOneAppAuthenticatedFromSession} from '@/store/one-app-session-auth';
import Unavailable from '@/router/Unavailable';
import Root from '@/app/Root';
import {redirectToLoginPage} from '@/utils/url-utils';
import PIWIKUtils from '@/utils/piwik-utils';
import OneAppUserAPI from '@/user/user-oneapp-api';
import CommonUtils from '@/utils/common-utils';
import teladocEnvelopHeaders from '@/utils/teladoc-envelop-utils';
import {formatStatsigIdentity, getStatsigOptions} from '@/utils/statsig-utils';
import {
    IS_PROD,
    MEMBER_ID,
    TEST_ENV_HOST,
    IMPERSONATION,
    PIWIK_ENABLED,
    MEMBER_ID_PREV,
    BUNDLE_IDENTIFIER,
    ALLOWED_BUNDLE_IDENTIFIERS,
    HEALTHIESTYOU_BUNDLE_IDENTIFIER,
    MEMBER_ACTION_ID,
} from '@/config';

// New Relic first, so the agent is watching before the rest of this module runs.
NewRelicClient.init({
    agentId: window?.process?.env?.AGENT_ID ?? process.env.AGENT_ID,
    trustKey: window?.process?.env?.TRUST_KEY ?? process.env.TRUST_KEY,
    accountId: window?.process?.env?.ACCOUNT_ID ?? process.env.ACCOUNT_ID,
    licenseKey: window?.process?.env?.LICENSE_KEY ?? process.env.LICENSE_KEY,
    applicationId:
        window?.process?.env?.APPLICATION_ID ?? process.env.APPLICATION_ID,
});

const brandColor = OneAppUserAPI.getBrandColor();

if (brandColor) {
    const observer = new MutationObserver((mutations, observerInstance) => {
        const rootElement = document.querySelector(
            ':root[data-theme="pli"][data-mode="light"]'
        );

        if (rootElement) {
            rootElement.style.setProperty(
                '--color-brand-interactive-primary-shade',
                `${brandColor}`
            );
            observerInstance.disconnect();
        }
    });

    observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
    });
}
// Set the data-theme attribute on the html element

if (
    !ALLOWED_BUNDLE_IDENTIFIERS.includes(
        CommonUtils.getCookie({key: BUNDLE_IDENTIFIER})
    )
) {
    document.documentElement.setAttribute('data-theme', 'pli');
}

const {
    loadingText,
} = require(`@teladoc/fe-i18n/locales/${CommonUtils.getOneAppLocaleCookie()}/app.json`);

const IS_DEV =
    (window?.process?.env?.NODE_ENV ?? process.env.NODE_ENV) === 'development';

const promises = [];
const {store, persistor} = create();
const fetchToken = Arg('fetchToken');
// Initialize TELADOC_ENVELOP with a Teladoc-Envelop header that includes member_action_id for observability.
const memberActionId = teladocEnvelopHeaders();

APIUtils.init({
    baseUrl: window?.process?.env?.API_URL ?? process.env.API_URL,
    additionalHeaders: {
        'Content-Type': 'application/json',
        'Accept-Language': CommonUtils.getOneAppLocaleCookie(),
    },
});

NewRelicUtils.setCustomAttribute(MEMBER_ACTION_ID, memberActionId);

ChatAPI.init(
    window?.process?.env?.SENDBIRD_APP_ID ?? process.env.SENDBIRD_APP_ID
);

mixpanel.init(
    window?.process?.env?.MIXPANEL_TOKEN ?? process.env.MIXPANEL_TOKEN
);

// nginx rewrites this placeholder per request. The vendor needs it to inject
// tags under a CSP without 'unsafe-inline'.
const nonce = document.querySelector('meta[name="csp-nonce"]')?.content;
const bundleIdentifier = CommonUtils.getCookie({key: BUNDLE_IDENTIFIER});
const defaultAmplitudeApiKey =
    window?.process?.env?.AMPLITUDE_API_KEY ?? process.env.AMPLITUDE_API_KEY;
const bundleAmplitudeApiKeys = {
    [HEALTHIESTYOU_BUNDLE_IDENTIFIER]:
        window?.process?.env?.HY_AMPLITUDE_API_KEY ??
        process.env.HY_AMPLITUDE_API_KEY,
};

AmplitudeClient.init({
    debug: !IS_PROD,
    memberId: OneAppUserAPI.getMemberId(),
    surveys: {
        ...(nonce && {nonce}),
        locale: CommonUtils.getOneAppLocaleCookie(),
    },
    apiKey: bundleAmplitudeApiKeys[bundleIdentifier] || defaultAmplitudeApiKey,
    proxyUrl:
        window?.process?.env?.AMPLITUDE_PROXY_URL ??
        process.env.AMPLITUDE_PROXY_URL,
});

/**
 * Piwik loads async scripts; defer until after first paint to reduce main-thread
 * contention with React bootstrap (New Relic + Sendbird + Mixpanel stay eager).
 */
function runAfterFirstPaint(fn) {
    if (typeof window === 'undefined') {
        return;
    }

    if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(fn);
        });
    } else {
        setTimeout(fn, 0);
    }
}

function initPiwikWhenEnabled() {
    if (
        (IS_PROD || CommonUtils.getCookie({key: PIWIK_ENABLED})) &&
        !OneAppUserAPI.isCanadaInstance()
    ) {
        const siteId =
            window?.process?.env?.PIWIK_SITE_ID ?? process.env.PIWIK_SITE_ID;

        PIWIKUtils.init(window, document, siteId);
        PIWIKUtils.piwikSetup(document, siteId);
        PIWIKUtils.loadScript(siteId);
    }
}

// Apptentive: load after first paint so CCM bootstrap (promises) is not blocked.
async function initApptentive() {
    try {
        await ApptentiveUtils.init({
            appId:
                window?.process?.env?.APPTENTIVE_APP_ID ??
                process.env.APPTENTIVE_APP_ID,
            options: {
                // eslint-disable-next-line camelcase
                app_release: {
                    version:
                        window?.process?.env?.VERSION ?? process.env.VERSION,
                },
            },
        });
    } catch (error) {
        if (IS_DEV) {
            // eslint-disable-next-line no-console
            console.log('[ERROR: APPTENTIVE]', error);
        }
    }
}

runAfterFirstPaint(initPiwikWhenEnabled);
runAfterFirstPaint(initApptentive);

StatsigClient.init(
    window?.process?.env?.STATSIG_CLIENT_KEY || process.env.STATSIG_CLIENT_KEY,
    {},
    getStatsigOptions()
);

const InitializedApp = () => {
    const dispatch = useDispatch();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        Promise.all(promises).then(data => {
            const {app, user} = data?.[0] || {};

            if (CommonUtils.isOneAppLocaleCookieSet()) {
                delete user?.userLocale;
            } else {
                CommonUtils.setOneAppLocaleCookie(user?.userLocale);
            }

            dispatch(appUpdate(app));
            dispatch(userUpdate(user));

            StatsigClient.updateUser(formatStatsigIdentity(user));

            const memberId = OneAppUserAPI.getMemberId();

            if (memberId) {
                AmplitudeClient.identify(memberId);
            }

            setIsInitialized(true);
        });
    }, [dispatch]);

    if (
        window.location.href?.includes(CONDITION_MANAGEMENT_PREFIX) &&
        !isInitialized
    ) {
        return <Loader loadingText={loadingText} />;
    }

    return <Root />;
};

function bootOneApp() {
    if (process.env.NODE_ENV === 'development') {
        const {initLocalDev} = require('@one-app-local-dev/init-local-dev');

        if (initLocalDev()) {
            return;
        }
    }

    if (!getIsOneAppAuthenticatedFromSession()) {
        redirectToLoginPage();
    } else {
        if (document.location.pathname === '/') {
            window.history.pushState({}, '', HOME_PATH);
        }

        const impersonationDetails = OneAppUserAPI.getImpersonationDetails();

        if (impersonationDetails?.impersonatorId) {
            MixpanelUtils.setSuperProps({
                impersonation: true,
            });

            StorageUtils.set({
                key: IMPERSONATION,
                value: impersonationDetails,
                type: 'session',
                useNative: true,
            });

            const host =
                typeof window !== 'undefined' ? window.location.hostname : ''; // Get current hostname

            if (host.includes(TEST_ENV_HOST)) {
                const impersonationObject = {
                    /* eslint-disable camelcase */
                    impersonator_type: encodeURI(
                        impersonationDetails.impersonatorType
                    ),
                    impersonator_type_id: Number.parseInt(
                        impersonationDetails.impersonatorId,
                        10
                    ),
                    impersonated_type: encodeURI(
                        impersonationDetails.impersonatedType
                    ),
                    impersonated_type_id: Number.parseInt(
                        impersonationDetails.impersonatedId,
                        10
                    ),
                    impersonated_name: encodeURI(
                        impersonationDetails.impersonatedName
                    ),
                    /* eslint-enable camelcase */
                };

                // Set cookie with explicit domain
                StorageUtils.set({
                    key: IMPERSONATION,
                    value: impersonationObject,
                    type: 'cookie',
                    useNative: true,
                    stringify: true,
                    cookieOptions: {
                        domain: `.${TEST_ENV_HOST}`,
                        sameSite: 'lax',
                        secure: true,
                    },
                });
            }
        } else {
            MixpanelUtils.setSuperProps({
                impersonation: false,
            });
        }

        if (
            OneAppUserAPI.getTdocAuthToken() !==
            OneAppUserAPI.getPrevMemberTdocAuthToken()
        ) {
            OneAppUserAPI.removeOneAppCookies();
        }

        if (
            isNil(OneAppUserAPI.getCurrentCcmAccessToken()) || // case 1 - no ccm token set
            (OneAppUserAPI.getCurrentCcmAccessToken() && // case 2 - ccm token set and ttl is NOT valid
                !OneAppUserAPI.isCcmTokenTtlValid()) ||
            CommonUtils.getLocalStorage(MEMBER_ID_PREV) !==
                CommonUtils.getCookie({key: MEMBER_ID}) ||
            fetchToken // case 4 -fetchToken Param exist in url then we need to get new CCM Token.
        ) {
            promises.push(
                OneAppUserAPI.initCcmFlow().then(response => {
                    return (
                        response &&
                        UserAPI.loadInitialDataForOneApp({
                            response,
                            isOneApp: true,
                            isOneAppAuthenticated:
                                hasNonEmptyValue(
                                    OneAppUserAPI.getTdocAuthToken()
                                ) &&
                                hasNonEmptyValue(
                                    OneAppUserAPI.getBundleIdentifier()
                                ),
                            impersonationInfo: impersonationDetails,
                        })
                    );
                })
            );
        } else {
            OneAppUserAPI.loadInitialData(promises, impersonationDetails);
        }
    }

    if (EnvironmentUtils.isSafari()) {
        // Safari is the new IE, we need to be able to target it directly to fix random annoying issues
        document.querySelector('html')?.classList.add('Safari');
    }

    withResolvers.shim();

    render(
        <ErrorBoundary errorComponent={<Unavailable />}>
            <PulseApp
                i18n={{
                    ...i18n,
                    interpolation: {
                        defaultVariables: {
                            companyName: 'Teladoc Health',
                            inTouchDevice: 'Teladoc Health',
                        },
                    },
                }}
                build={{
                    key: 'mp-build-tag',
                    value:
                        window?.process?.env?.BUILD_TAG ??
                        process.env.BUILD_TAG,
                }}
                loadingText={loadingText}
                render={({data, status}) => {
                    const App = () => (
                        <Provider store={store}>
                            <PersistGate
                                loading={null}
                                persistor={persistor}
                                onBeforeLift={() => {
                                    store.dispatch(
                                        userUpdate({
                                            isOneAppAuthenticated:
                                                getIsOneAppAuthenticatedFromSession(),
                                        })
                                    );
                                }}
                            >
                                <StatsigProvider>
                                    <InitializedApp />
                                </StatsigProvider>
                            </PersistGate>
                        </Provider>
                    );

                    switch (status) {
                        case STATUSES.INITIALIZED:
                            return App();
                        case STATUSES.ERROR:
                            if (IS_DEV) {
                                // eslint-disable-next-line no-console
                                console.log('[ERROR: INITIAL LOAD]', data);
                            }

                            // ERROR Handling
                            return <Unavailable />;
                    }
                }}
            />
        </ErrorBoundary>,
        document.getElementById('root')
    );
}

bootOneApp();
